import { format } from 'date-fns'

import type { DatePickerProps, DatePickerRangeProps } from '@/modules/drivers/types'

export const formatApiDate = (date: Date): string => {
  return encodeURIComponent(format(date, 'YYYY-MM-DD 00:00'))
}

const pad = (n: number) => String(n).padStart(2, '0')

const toEdge = (date: Date): DatePickerProps => ({
  fullDate: format(date, 'DD-MM-YYYY'),
  day: date.getDate(),
  month: date.getMonth() + 1,
  year: date.getFullYear(),
})

export const edgeToIsoDate = (edge: DatePickerProps | null): string | null => {
  if (!edge || edge.year == null || edge.month == null || edge.day == null) {
    return null
  }
  return `${edge.year}-${pad(edge.month)}-${pad(edge.day)}`
}

export const monthStringToRange = (
  monthStr: string,
): DatePickerRangeProps => {
  const [y, m] = monthStr.split('-').map(Number)
  return {
    startDate: toEdge(new Date(y, m - 1, 1)),
    endDate: toEdge(new Date(y, m, 0)),
  }
}

export const isoDatesToRange = (
  from: string,
  to: string,
): DatePickerRangeProps | null => {
  const parse = (iso: string) => {
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!m) return null
    const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    return isNaN(date.getTime()) ? null : date
  }
  const start = parse(from)
  const end = parse(to)
  if (!start || !end) return null
  return { startDate: toEdge(start), endDate: toEdge(end) }
}

export const yearStringToRange = (yearStr: string): DatePickerRangeProps => {
  const y = Number(yearStr)
  return {
    startDate: toEdge(new Date(y, 0, 1)),
    endDate: toEdge(new Date(y, 11, 31)),
  }
}

export const clampDayToMonth = (monthStr: string, day: number): string => {
  const [y, m] = monthStr.split('-').map(Number)
  const lastDay = new Date(y, m, 0).getDate()
  return `${monthStr}-${pad(Math.min(Math.max(day, 1), lastDay))}`
}

export const rangeCoversDate = (
  range: DatePickerRangeProps | null,
  isoDate: string,
): boolean => {
  const from = edgeToIsoDate(range?.startDate ?? null)
  const to = edgeToIsoDate(range?.endDate ?? null)
  if (!from || !to) return false
  return from <= isoDate && isoDate <= to
}

export const rangeWithinMonth = (
  range: DatePickerRangeProps | null,
  monthStr: string,
): boolean => {
  const from = edgeToIsoDate(range?.startDate ?? null)
  const to = edgeToIsoDate(range?.endDate ?? null)
  if (!from || !to) return false
  return from.startsWith(monthStr) && to.startsWith(monthStr)
}


export type DateState = {
  single: string | null
  range: DatePickerRangeProps | null
  month: string | null
  year: string | null
}

const dayOf = (isoDate: string) => Number(isoDate.substring(8, 10))

export const applySingle = (s: DateState, d: string | null): DateState => {
  if (!d) return { ...s, single: d }
  const month = d.substring(0, 7)

  const range = rangeCoversDate(s.range, d) ? s.range : monthStringToRange(month)
  return { ...s, single: d, month, year: d.substring(0, 4), range }
}

export const applyRange = (
  s: DateState,
  d: DatePickerRangeProps | null,
): DateState => {
  const start = edgeToIsoDate(d?.startDate ?? null)
  if (!d || !start) return { ...s, range: d }

  return {
    ...s,
    range: d,
    month: start.substring(0, 7),
    year: start.substring(0, 4),
    single: s.single && rangeCoversDate(d, s.single) ? s.single : start,
  }
}

export const applyMonth = (s: DateState, d: string | null): DateState => {
  if (!d) return { ...s, month: null }
  const range = rangeWithinMonth(s.range, d) ? s.range : monthStringToRange(d)
  const single = s.single ? clampDayToMonth(d, dayOf(s.single)) : s.single
  return { ...s, month: d, year: d.substring(0, 4), range, single }
}

export const applyYear = (s: DateState, d: string | null): DateState => {
  if (!d) return { ...s, year: null }
  const month = s.month ? `${d}${s.month.substring(4)}` : s.month
  const single = s.single
    ? clampDayToMonth(`${d}${s.single.substring(4, 7)}`, dayOf(s.single))
    : s.single
  const range =
    month && !rangeWithinMonth(s.range, month)
      ? monthStringToRange(month)
      : s.range
  return { ...s, year: d, month, single, range }
}


export const resolveDateRange = (
  s: Pick<DateState, 'range' | 'month' | 'year'>,
  now: Date,
): { from: string; to: string } => {
  const from = edgeToIsoDate(s.range?.startDate ?? null)
  const to = edgeToIsoDate(s.range?.endDate ?? null)
  if (from && to) return { from, to }

  if (s.month) {
    const monthRange = monthStringToRange(s.month)
    return {
      from: edgeToIsoDate(monthRange.startDate)!,
      to: edgeToIsoDate(monthRange.endDate)!,
    }
  }
  if (s.year) return { from: `${s.year}-01-01`, to: `${s.year}-12-31` }

  const currentMonth = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`
  const monthRange = monthStringToRange(currentMonth)
  return {
    from: edgeToIsoDate(monthRange.startDate)!,
    to: edgeToIsoDate(monthRange.endDate)!,
  }
}
